import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const SchoolLogoSVG = () => (
  <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" width="48" height="48">
    <rect width="60" height="60" rx="14" fill="url(#grad)"/>
    <defs><linearGradient id="grad" x1="0" y1="0" x2="60" y2="60"><stop stopColor="#1a73e8"/><stop offset="1" stopColor="#0f9d58"/></linearGradient></defs>
    <path d="M30 12L10 24l20 10 20-10L30 12zm0 16L14 20v8c0 7 8 12 16 12s16-5 16-12v-8L30 28z" fill="white" opacity="0.9"/>
  </svg>
);

const AuthLayout = ({ children, title, subtitle }) => (
  <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#f0f4ff,#e8f0fe)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'Poppins, sans-serif' }}>
    <div style={{ background: '#fff', borderRadius: 24, boxShadow: '0 8px 40px rgba(26,115,232,0.12)', padding: '40px 36px', width: '100%', maxWidth: 440 }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}><SchoolLogoSVG /></div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1f36', marginBottom: 6 }}>{title}</h1>
        <p style={{ fontSize: '0.85rem', color: '#6b7280' }}>{subtitle}</p>
      </div>
      {children}
    </div>
  </div>
);

export function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await login(form.email, form.password);
    setLoading(false);
    if (result.success) {
      const role = result.user.role;
      if (role === 'admin' || role === 'teacher') {
        navigate('/erp');
      } else {
        toast.success('Logged in successfully');
        navigate('/portal');
      }
    } else {
      toast.error(result.error);
    }
  };

  return (
    <AuthLayout title="Welcome Back" subtitle="Sign in to your school account">
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 6, color: '#1a1f36' }}>Email Address</label>
          <input type="email" required placeholder="you@school.com"
            value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e0e7ff', borderRadius: 8, fontFamily: 'Poppins', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
            onFocus={e => e.target.style.borderColor = '#1a73e8'} onBlur={e => e.target.style.borderColor = '#e0e7ff'} />
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 6, color: '#1a1f36' }}>Password</label>
          <input type="password" required placeholder="••••••••"
            value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e0e7ff', borderRadius: 8, fontFamily: 'Poppins', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
            onFocus={e => e.target.style.borderColor = '#1a73e8'} onBlur={e => e.target.style.borderColor = '#e0e7ff'} />
        </div>
        <button type="submit" disabled={loading}
          style={{ width: '100%', padding: '12px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 10, fontFamily: 'Poppins', fontWeight: 700, fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
      </form>

      <div style={{ textAlign: 'center', marginTop: 20, fontSize: '0.82rem', color: '#6b7280' }}>
        Don't have an account? <Link to="/signup" style={{ color: '#1a73e8', fontWeight: 700 }}>Register</Link>
      </div>
      <div style={{ textAlign: 'center', marginTop: 12, fontSize: '0.82rem', color: '#6b7280' }}>
        <Link to="/" style={{ color: '#6b7280' }}>← Back to Website</Link>
      </div>

      {/* Demo credentials */}
      <div style={{ marginTop: 24, padding: '12px 16px', background: '#f8faff', borderRadius: 10, border: '1px solid #e0e7ff' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a73e8', marginBottom: 6 }}>Demo Credentials</div>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Admin: admin@school.com / password</div>
      </div>
    </AuthLayout>
  );
}

export function SignupPage() {
  const [form, setForm] = useState({ full_name: '', email: '', password: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/signup', form);
      toast.success('Account created! Admin will assign your role.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally { setLoading(false); }
  };

  const fields = [
    { key: 'full_name', label: 'Full Name', type: 'text', placeholder: 'John Doe' },
    { key: 'email', label: 'Email Address', type: 'email', placeholder: 'you@example.com' },
    { key: 'phone', label: 'Phone Number', type: 'tel', placeholder: '9876543210' },
    { key: 'password', label: 'Password', type: 'password', placeholder: '••••••••' },
  ];

  return (
    <AuthLayout title="Create Account" subtitle="Register for school portal access">
      <div style={{ background: '#fef3e2', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: '0.78rem', color: '#b35c00', fontWeight: 600 }}>
        ⚠️ This creates a basic portal account. ERP access is assigned by admin only.
      </div>
      <form onSubmit={handleSubmit}>
        {fields.map(f => (
          <div key={f.key} style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 6, color: '#1a1f36' }}>{f.label}</label>
            <input type={f.type} required={f.key !== 'phone'} placeholder={f.placeholder}
              value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e0e7ff', borderRadius: 8, fontFamily: 'Poppins', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.target.style.borderColor = '#1a73e8'} onBlur={e => e.target.style.borderColor = '#e0e7ff'} />
          </div>
        ))}
        <button type="submit" disabled={loading}
          style={{ width: '100%', marginTop: 8, padding: '12px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 10, fontFamily: 'Poppins', fontWeight: 700, fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Creating...' : 'Create Account'}
        </button>
      </form>
      <div style={{ textAlign: 'center', marginTop: 20, fontSize: '0.82rem', color: '#6b7280' }}>
        Already have an account? <Link to="/login" style={{ color: '#1a73e8', fontWeight: 700 }}>Login</Link>
      </div>
      <div style={{ textAlign: 'center', marginTop: 8, fontSize: '0.82rem' }}>
        <Link to="/" style={{ color: '#6b7280' }}>← Back to Website</Link>
      </div>
    </AuthLayout>
  );
}
